import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Button, Paper } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  EnumCommodity,
  EnumTipoDocumentoFiscal,
  EnumTipoDocumentoIdentidad,
  EnumTipoResidencia,
  EnumTipoSujeto,
  EnumTipoSuministro
} from '../../../api'
import { EnumTipoNotificacion } from '../../../api/oData/enums/enumTipoNotificacion'
import ButtonWithPermissions from '../../../components/buttonWithPermissions/buttonWithPermissions'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumEnumToValueNumber, enumValueToKey } from '../../../helpers'
import { hasPermission } from '../../../helpers/permissionsHelper'
import { getEmailRegex } from '../../../helpers/regexHelper'
import { missingFieldsForm } from '../../../helpers/validateFormHelper'
import { useConfirmationDialog } from '../../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { Resources } from '../../../model/RBAC/constants'
import { SearchSelectOption } from '../../../model/searchSelectOption'
import {
  fetchClientDataByID,
  getActiveCommodities,
  getCustomerDomain,
  notifyError,
  RootStoreModel
  //storeActiveTabForCurrentPage
} from '../../../redux'
import { validateDocument } from '../client.helper'
import { listIdDocumentItemMapper } from '../client.mapper'
import {
  checkDuplicates,
  fetchClientSubtypes,
  fetchCommodityTypes,
  fetchCountries,
  fetchDocumentTypes,
  fetchLanguage,
  fetchNotificationTypes,
  fetchPersonTypes,
  fetchPostalCodes,
  fetchResidenceTypes,
  getPostalAddressByID,
  saveClient
} from '../client.service'
import { ClientGeneralDataFormModel, IdentificationDocumentModel } from '../clients.model'

export const ClientAddPage: React.FC<any> = props => {
  const history = useHistory()
  const [t] = useTranslation(['clients', 'globalerisLibrary', 'enums'])
  const subjectType: EnumTipoSujeto =
    props.location.state !== undefined
      ? props.location.state.subjectType
      : EnumTipoSujeto.ClienteFinal

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const { info } = useNotify()
  const { getInformation, closeInformation } = useConfirmationDialog()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const [display, setDisplay] = useState<boolean>(false)
  const [clientGeneralDataForm, setClientGeneralDataForm] = useState<
    ClientGeneralDataFormModel | undefined
  >(undefined)

  const [clientSubtypes, setClientSubtypes] = useState<SelectOption[]>([])
  const [countries, setCountries] = useState<SelectOption[]>([])
  const [personTypes, setPersonTypes] = useState<SelectOption[]>([])
  const [documentTypes, setDocumentTypes] = useState<IdentificationDocumentModel[]>([])
  const [language, setLanguage] = useState<SelectOption[]>([])
  const [notificationTypes, setNotificationTypes] = useState<SelectOption[]>([])

  const [residenceTypes, setResidenceTypes] = useState<SelectOption[]>([])
  const [commodityTypes, setCommodityTypes] = useState<SelectOption[]>([])

  const commodities = getActiveCommodities()
  const initialCommodityType: EnumTipoSuministro | undefined =
    commodities && commodities?.length === 1
      ? enumValueToKey(EnumCommodity, commodities[0]) == EnumCommodity.GasNatural
        ? EnumTipoSuministro.Gas
        : EnumTipoSuministro.Luz
      : undefined

  const [commodityType, setCommodityType] = useState<EnumTipoSuministro | undefined>(
    initialCommodityType
  )

  const [documentIdIsValid, setDocumentIdIsValid] = useState<{
    isLoading: boolean
    valid: boolean
    reason: string | null
  }>({
    isLoading: false,
    valid: true,
    reason: null
  })
  const [emailIsValid, setEmailIsValid] = useState<boolean>(true)
  const [emailIsRequired, setEmailIsRequired] = useState<boolean>(false)

  const [postalCodeIsValid, setPostalCodeIsValid] = useState<boolean>(true)

  useEffect(() => {
    const showAnnouncement = async () => {
      await getInformation(InformationDialogs.COMMUNICATION_NEW_PAGE_CLIENT_ADD).then(res =>
        closeInformation()
      )
    }

    showAnnouncement()
  }, [])

  useEffect(() => {
    setIsFetching(true)
    const getPersonTypes = async () => {
      await fetchPersonTypes().then(response => {
        setPersonTypes(response.data)
      })
    }

    const getClientSubtypes = async () => {
      await fetchClientSubtypes(subjectType).then(response => {
        setClientSubtypes(response.data)
      })
    }

    const getCountries = async () => {
      await fetchCountries().then(response => {
        if (response.success && response.data) {
          if (response.data.length > 0) {
            setCountries(response.data)
          } else {
            setCountries([{ value: 1, label: 'ESPAÑA' } as SelectOption])
          }
        }
      })
    }

    const getDocumentTypes = async () => {
      await fetchDocumentTypes().then(response => {
        setDocumentTypes(response.data)
      })
    }

    const getLanguage = async () => {
      await fetchLanguage().then(response => {
        setLanguage(response.data)
      })
    }

    const getNotificationTypes = async () => {
      await fetchNotificationTypes().then(response => {
        setNotificationTypes(response.data)
      })
    }

    const getResidenceTypes = async () => {
      await fetchResidenceTypes().then(response => {
        setResidenceTypes(response.data)
      })
    }

    const getCommodityTypes = async () => {
      await fetchCommodityTypes().then(response => {
        setCommodityTypes(response.data)
      })
    }

    Promise.all([
      getPersonTypes(),
      getClientSubtypes(),
      getCountries(),
      getDocumentTypes(),
      getLanguage(),
      getNotificationTypes(),
      getResidenceTypes(),
      getCommodityTypes()
    ]).then(responses => {
      setIsFetching(false)
      setClientGeneralDataForm({
        personType: EnumTipoDocumentoFiscal.Fisica,
        nationality: 1
      })
      setCommodities()
      setDisplay(false)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany, activeBalanceZone])

  const validateDoc = async (doc: string, docType?: EnumTipoDocumentoIdentidad) => {
    setDocumentIdIsValid(prevState => ({ ...prevState, isLoading: true }))

    const isValid = docType ? validateDocument(doc, docType) : true
    const isDuplicate =
      doc !== ''
        ? await checkDuplicates(doc, undefined, subjectType)
            .then(res => {
              if (!res.success) {
                // error api
              }
              return res.data
            })
            .finally(() => {
              setDocumentIdIsValid(prevState => ({ ...prevState, isLoading: false }))
            })
        : false

    if (isValid && !isDuplicate) {
      setDocumentIdIsValid({ valid: true, reason: null, isLoading: false })
    } else {
      setDocumentIdIsValid({
        isLoading: false,
        valid: false,
        reason: isDuplicate
          ? t('clients:notifications.create.invalidDocumentIdDuplicate')
          : t('clients:notifications.create.invalidDocumentId')
      })
    }
  }

  const setCommodities = () => {
    if (initialCommodityType) {
      // If active company has only 1 commodity, set Commodity Input with that value and remove not valid options.
      setClientGeneralDataForm(prevState => ({ ...prevState, commodity: initialCommodityType }))
      setCommodityTypes(options => options.filter(o => o.value === initialCommodityType))
    }

    setCommodityType(initialCommodityType)
  }

  const onSaveClient = async () => {
    let clientID = 0
    if (clientGeneralDataForm) {
      if (missingFieldsForm(mainFormDefinition(), clientGeneralDataForm)) {
        notifyError(t('clients:notifications.create.errorInvalidForm'))
      } else {
        await saveClient({
          ...clientGeneralDataForm,
          phoneNumber: clientGeneralDataForm?.phoneNumber?.replaceAll(' ', ''),
          businessName:
            clientGeneralDataForm.personType == EnumTipoDocumentoFiscal.Juridica
              ? clientGeneralDataForm.businessName
              : undefined,
          name:
            clientGeneralDataForm.personType == EnumTipoDocumentoFiscal.Fisica
              ? clientGeneralDataForm.name
              : undefined,
          surname1:
            clientGeneralDataForm.personType == EnumTipoDocumentoFiscal.Fisica
              ? clientGeneralDataForm.surname1
              : undefined,
          surname2:
            clientGeneralDataForm.personType == EnumTipoDocumentoFiscal.Fisica
              ? clientGeneralDataForm.surname2
              : undefined,
          commodity:
            subjectType == EnumTipoSujeto.Comercial
              ? null
              : commodityType
                ? commodityType
                : clientGeneralDataForm.commodity,
          residenceType: clientGeneralDataForm.residenceType ?? EnumTipoResidencia.Residente
        }).then(response => {
          if (response.success) {
            info(t('clients:notifications.create.success'))
            clientID = response.data!.id
          } else {
            notifyError(t('clients:notifications.create.error'))
          }
        })
      }
    }
    return clientID
  }

  const updateAddress = async (option: SearchSelectOption | null) => {
    if (!option) {
      setClientGeneralDataForm(prevState => ({
        ...prevState,
        municipalityId: undefined,
        municipality: '',
        provinceId: undefined,
        province: '',
        countryId: undefined,
        country: '',
        postalCode: ''
      }))
      return
    }

    const address = await getPostalAddressByID(+option.value).catch(e => {
      console.log(e)
    })

    if (address) {
      setClientGeneralDataForm(prevState => ({
        ...prevState,
        municipalityId: address.municipality.id,
        municipality: address.municipality.description,
        provinceId: address.province.id,
        province: address.province.description,
        countryId: address.country.id,
        country: address.country.description,
        postalCode: option.textValue
      }))
    }
  }

  const validateEmail = (emailValue: string | number) => {
    const emailStr = emailValue?.toString() || ''

    if (emailIsRequired) {
      if (!emailStr || emailStr.trim() === '') {
        setEmailIsValid(false)
      } else {
        const regex = new RegExp(getEmailRegex())
        setEmailIsValid(regex.test(emailStr.trim()))
      }
    } else {
      // Si el email no es requerido, validar solo si hay contenido
      if (emailStr) {
        const regex = new RegExp(getEmailRegex())
        setEmailIsValid(regex.test(emailStr.trim()))
      } else {
        setEmailIsValid(true)
      }
    }

    // Actualizar el estado del formulario
    setClientGeneralDataForm(prevFormState => ({
      ...prevFormState,
      email: emailStr.trim()
    }))
  }
  //Handlers region Ends

  //Form config Starts
  const options: FormOptions = {
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('clients:forms.add.analytics')
    }
  }

  const sectionsAuxiliaryData: Section[] = [
    {
      title: t('clients:forms.add.titles.switching'),
      inputs: [
        {
          name: 'nationality',
          label: t('clients:forms.add.inputs.nationality'),
          type: DataType.SELECT,
          defaultValue: 1,
          selectOptions: countries
        },
        {
          name: 'residenceType',
          label: t('clients:forms.add.inputs.residenceType'),
          type: DataType.SELECT,
          selectOptions: residenceTypes
        }
      ]
    },
    {
      title: t('clients:forms.add.titles.codes'),
      inputs: [
        {
          name: 'clientCode',
          label: t('clients:forms.add.inputs.clientCode'),
          type: DataType.TEXT,
          required: false
        },
        {
          name: 'erpCode',
          label: t('clients:forms.add.inputs.erpCode'),
          type: DataType.TEXT,
          required: false
        }
      ]
    }
  ]

  const mainFormDefinition = (): Section[] => {
    let list: Section[] = [
      {
        title: t('clients:forms.add.titles.segmentation'),
        inputs: [
          {
            name: 'clientType',
            label: t('clients:forms.add.inputs.clientType'),
            type: DataType.SELECT,
            selectOptions: clientSubtypes,
            required: true
          },
          {
            name: 'commodity',
            label: t('clients:forms.add.inputs.commodity'),
            type: DataType.SELECT,
            selectOptions: commodityTypes,
            readOnly: !!initialCommodityType,
            display: subjectType !== EnumTipoSujeto.Comercial, //mostrar el desplegable de tipo de suministro si la empresa tiene más de una commodity
            required: commodityType === undefined
          },
          {
            name: 'personType',
            label: t('clients:forms.add.inputs.personType'),
            type: DataType.RADIO,
            selectOptions: personTypes,
            required: true,
            onChange: (value, formState: any) => {
              if (value == EnumTipoDocumentoFiscal.Fisica) {
                setDisplay(false)
              } else {
                setDisplay(true)
              }
            }
          }
        ]
      },
      {
        title: t('clients:forms.add.titles.holderData'),
        inputs: [
          {
            name: 'name',
            label: t('clients:forms.add.inputs.name'),
            type: DataType.TEXT,
            required: !display,
            display: !display
          },
          {
            name: 'surname1',
            label: t('clients:forms.add.inputs.surname1'),
            type: DataType.TEXT,
            required: !display,
            display: !display
          },
          {
            name: 'surname2',
            label: t('clients:forms.add.inputs.surname2'),
            type: DataType.TEXT,
            display: !display
          },
          {
            name: 'businessName',
            label: t('clients:forms.add.inputs.businessName'),
            type: DataType.TEXT,
            required: display,
            display: display
          },
          {
            name: 'documentType',
            label: t('clients:forms.add.inputs.documentType'),
            type: DataType.SELECT,
            selectOptions: listIdDocumentItemMapper(documentTypes),
            required: true,
            onChange: docType => {
              validateDoc(clientGeneralDataForm?.documentId ?? '', docType as number)
              setClientGeneralDataForm(prevFormState => ({
                ...prevFormState,
                documentType: docType as EnumTipoDocumentoIdentidad
              }))
            }
          },
          {
            name: 'documentId',
            label: t('clients:forms.add.inputs.documentId'),
            type: DataType.TEXT,
            errorText:
              documentIdIsValid.reason ?? t('clients:notifications.create.invalidDocumentId'),
            errorState: !documentIdIsValid.valid,
            required: true,
            onBlur: async (newDocumentId: any) => {
              validateDoc(newDocumentId, clientGeneralDataForm?.documentType)
              setClientGeneralDataForm(prevFormState => ({
                ...prevFormState,
                documentId: newDocumentId
              }))
            }
          }
        ]
      },
      {
        title: t('clients:forms.add.titles.contactData'),
        inputs: [
          {
            name: 'postalAddress',
            label: t('clients:forms.add.inputs.postalAddress'),
            type: DataType.TEXT,
            required: true
          },
          {
            name: 'postalCodeSelect',
            label: t('clients:forms.add.inputs.postalCode'),
            type: DataType.SEARCHFIELD,
            apiCall: fetchPostalCodes,
            maxLength: 5,
            required: true,
            clearable: true,
            onChange: newCode => {
              const option = newCode as any
              updateAddress(option as SearchSelectOption | null)
              // validatePCode(`${newCode}`, clientGeneralDataForm?.provinceId)
            }
          },
          {
            name: 'municipality',
            label: t('clients:forms.add.inputs.municipality'),
            type: DataType.TEXT,
            required: true,
            readOnly: true
          },
          {
            name: 'province',
            label: t('clients:forms.add.inputs.province'),
            type: DataType.TEXT,
            required: true,
            readOnly: true
          },
          {
            name: 'country',
            label: t('clients:forms.add.inputs.country'),
            type: DataType.TEXT,
            required: true,
            readOnly: true
          },
          {
            name: 'email',
            label: t('clients:forms.add.inputs.email'),
            type: DataType.TEXT,
            regexp: getEmailRegex(),
            required: emailIsRequired,
            errorText: t('clients:notifications.create.invalidEmail'),
            onBlur: validateEmail
          },
          {
            name: 'phoneNumber',
            label: t('clients:forms.add.inputs.phoneNumber'),
            type: DataType.TEXT,
            maxLength: 16,
            // 111 222 333 / +34 111 222 333 / +39 11 2222 3333 / +1 111 222 3334
            regexp: '^$|^(((\\d\\s?){3}|[+]((\\d\\s?){1,3})(\\d\\s?){3})(\\s?(\\d\\s?){6,10}))$',
            errorText: t('clients:notifications.create.invalidPhoneNumber'),
            helpText: 'Formato +999999999999 o 999999999'
          },
          {
            name: 'language',
            label: t('clients:forms.add.inputs.language'),
            type: DataType.SELECT,
            selectOptions: language,
            clearable: true
          },
          {
            name: 'notifications',
            label: t('clients:forms.add.inputs.notifications'),
            type: DataType.SELECT,
            clearable: true,
            selectOptions: notificationTypes,
            onChange(value, formState) {
              const notifType = value as EnumTipoNotificacion
              setEmailIsRequired(
                notifType === EnumTipoNotificacion.EnvioCorreoElectronico ||
                  notifType === EnumTipoNotificacion.Ambos
              )

              // Siempre validar el email cuando cambie el tipo de notificación
              if (
                clientGeneralDataForm?.email !== undefined &&
                clientGeneralDataForm?.email !== ''
              ) {
                validateEmail(clientGeneralDataForm.email)
              } else if (
                notifType === EnumTipoNotificacion.EnvioCorreoElectronico ||
                notifType === EnumTipoNotificacion.Ambos
              ) {
                // Si el email está vacío pero ahora es requerido, marcarlo como inválido
                setEmailIsValid(false)
              } else {
                // Si el email no es requerido y está vacío, es válido
                setEmailIsValid(true)
              }

              setClientGeneralDataForm(prevFormState => ({
                ...prevFormState,
                notifications: +value
              }))
            }
          },
          {
            name: 'additionalInfo',
            label: t('clients:forms.add.inputs.additionalInfo'),
            type: DataType.LONGTEXT
          }
        ]
      }
    ]
    if (subjectType !== EnumTipoSujeto.Comercial) {
      list = list.concat(sectionsAuxiliaryData)
    }
    return list
  }

  //Functions Ends

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('clients:titleAdd')}>
        <Button
          size="small"
          variant="text"
          color="primary"
          disabled={isFetching}
          onClick={() => {
            history.push(NonAuthRoutes.Client)
          }}
        >
          {t('clients:buttonsAdd.cancel')}
        </Button>
        <ButtonWithPermissions
          size="small"
          color="primary"
          hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          disabled={
            isFetching ||
            !postalCodeIsValid ||
            !documentIdIsValid?.valid ||
            documentIdIsValid?.isLoading ||
            !emailIsValid
          }
          onClick={async () => {
            await onSaveClient().then(id => {
              if (id !== 0) {
                fetchClientDataByID(id)
                history.push(NonAuthRoutes.ContactInfo, { id: id, subjectType: subjectType })
              }
            })
          }}
        >
          {t('clients:buttonsAdd.addNewContact')}
        </ButtonWithPermissions>
        <ButtonWithPermissions
          size="small"
          color="primary"
          hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          disabled={
            isFetching ||
            !postalCodeIsValid ||
            !documentIdIsValid?.valid ||
            documentIdIsValid?.isLoading ||
            !emailIsValid
          }
          onClick={async () => {
            await onSaveClient().then(id => {
              if (id !== 0) {
                history.push(NonAuthRoutes.ClientFile, { clientID: id })
              }
            })
          }}
        >
          {t('clients:buttonsAdd.saveAndLeave')}
        </ButtonWithPermissions>
        <ButtonWithPermissions
          size="small"
          color="primary"
          variant="contained"
          hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          disabled={
            isFetching ||
            !postalCodeIsValid ||
            !documentIdIsValid?.valid ||
            documentIdIsValid?.isLoading ||
            !emailIsValid
          }
          onClick={async () => {
            await onSaveClient().then(id => {
              if (id !== 0) {
                const commodityID =
                  subjectType == EnumTipoSujeto.Comercial
                    ? null
                    : (enumEnumToValueNumber(EnumTipoSuministro, commodityType) ??
                      clientGeneralDataForm?.commodity)

                history.push(NonAuthRoutes.ContractAdd, {
                  clientID: id,
                  commodity:
                    commodityID === EnumTipoSuministro.LuzyGas
                      ? EnumCommodity.GasNatural
                      : commodityID
                })
              }
            })
          }}
        >
          {t('clients:buttonsAdd.saveAndAddContract')}
        </ButtonWithPermissions>
      </PageHeader>
      <Paper elevation={6}>
        <Form
          title={t('clients:tabsClient.generalData')}
          sections={mainFormDefinition()}
          data={clientGeneralDataForm}
          options={options}
        />
      </Paper>
    </Page>
  )
}
